import { HttpMethod } from '@cplug/frontend-core/models'

export const FORBIDDEN_ACCESS_SESSION_KEY = 'cplug:forbidden-access'

export const FORBIDDEN_ACCESS_PAGE_PATH = '/dashboard/not-allowed'

export const FORBIDDEN_ACCESS_IGNORED_PERMISSIONS = [
  'read_feature_flag',
  'read_research_customer',
  'update_research_customer'
] as const

export function isIgnoredForbiddenPermission (permission: string): boolean {
  return (FORBIDDEN_ACCESS_IGNORED_PERMISSIONS as readonly string[]).includes(permission)
}

export interface ForbiddenAccessContext {
  missingPermissions: string[]
  blockedFrom: string | null
  blockedApiUrl: string | null
  blockedApiMethod: HttpMethod | null
}

export const EMPTY_FORBIDDEN_ACCESS_CONTEXT: ForbiddenAccessContext = {
  missingPermissions: [],
  blockedFrom: null,
  blockedApiUrl: null,
  blockedApiMethod: null
}

const VALID_HTTP_METHODS = new Set<string>(Object.values(HttpMethod))

function parseBlockedApiMethod (value: unknown): HttpMethod | null {
  if (typeof value !== 'string' || !VALID_HTTP_METHODS.has(value)) {
    return null
  }

  return value as HttpMethod
}

export function normalizeMissingPermissions (permissions: string[]): string[] {
  return [...new Set(
    permissions
      .filter(permission => permission.length > 0)
      .filter(permission => !isIgnoredForbiddenPermission(permission))
  )].sort()
}

function parseMissingPermissions (parsed: Partial<ForbiddenAccessContext & { missingPermission?: string | null }>): string[] {
  if (Array.isArray(parsed.missingPermissions)) {
    return normalizeMissingPermissions(parsed.missingPermissions)
  }

  if (typeof parsed.missingPermission === 'string' && parsed.missingPermission.length > 0) {
    return normalizeMissingPermissions([parsed.missingPermission])
  }

  return []
}

export function readForbiddenAccessFromSessionStorage (): ForbiddenAccessContext {
  if (typeof sessionStorage === 'undefined') {
    return { ...EMPTY_FORBIDDEN_ACCESS_CONTEXT }
  }

  const raw = sessionStorage.getItem(FORBIDDEN_ACCESS_SESSION_KEY)
  if (raw == null) {
    return { ...EMPTY_FORBIDDEN_ACCESS_CONTEXT }
  }

  try {
    const parsed = JSON.parse(raw) as Partial<ForbiddenAccessContext & { missingPermission?: string | null }>
    return {
      missingPermissions: parseMissingPermissions(parsed),
      blockedFrom: parsed.blockedFrom ?? null,
      blockedApiUrl: parsed.blockedApiUrl ?? null,
      blockedApiMethod: parseBlockedApiMethod(parsed.blockedApiMethod)
    }
  } catch {
    return { ...EMPTY_FORBIDDEN_ACCESS_CONTEXT }
  }
}

export function writeForbiddenAccessToSessionStorage (context: ForbiddenAccessContext): void {
  if (typeof sessionStorage === 'undefined') {
    return
  }

  sessionStorage.setItem(FORBIDDEN_ACCESS_SESSION_KEY, JSON.stringify({
    ...context,
    missingPermissions: normalizeMissingPermissions(context.missingPermissions)
  }))
}

export function removeForbiddenAccessFromSessionStorage (): void {
  if (typeof sessionStorage === 'undefined') {
    return
  }

  sessionStorage.removeItem(FORBIDDEN_ACCESS_SESSION_KEY)
}
